import { Button } from "@/components/ui/Button";
import { formatMoneyShort } from "@/lib/money";

export interface PickerMonthlySale {
  month: string;
  revenue: number;
  quantity: number;
}

// One row of an add-product picker, as served by Plan::ProductPickerRows.
export interface PickerProduct {
  external_id: string;
  name: string;
  short_name: string | null;
  target_asp: { price: number; currency: string } | null;
  total_stock: number;
  channel_stock: number;
  monthly_sales: PickerMonthlySale[];
  in_plan: boolean;
  placed_node_id: string | null;
  placed_node_name: string | null;
}

function formatQty(n: number): string {
  if (!n) return "—";
  return n.toLocaleString("en-US");
}

function formatMonthLabel(iso: string): string {
  const [y, m] = iso.split("-").map((s) => parseInt(s, 10));
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

interface TableProps {
  months: string[] | null;
  products: PickerProduct[] | null;
  channelName: string;
  planCurrency?: string | null;
  loading: boolean;
  emptyMessage: string;
  renderAction: (product: PickerProduct) => React.ReactNode;
}

// The picker's product table — ID, name, target ASP, stock, three months of
// sales — shared by the ByMonth leaf dialog and the pricing page's modal.
// Each caller renders its own last-column action.
export default function ProductPickerTable({
  months,
  products,
  channelName,
  planCurrency,
  loading,
  emptyMessage,
  renderAction,
}: TableProps) {
  const formatRevenue = (n: number) => (n ? formatMoneyShort(n, planCurrency) : "—");
  const monthCount = months?.length || 3;

  return (
    <table className="w-full text-xs border-collapse" style={{ tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: 80 }} />
        <col />
        <col style={{ width: 90 }} />
        <col style={{ width: 80 }} />
        <col style={{ width: 70 }} />
        {Array.from({ length: monthCount }).map((_, i) => (
          <col key={i} style={{ width: 80 }} />
        ))}
        <col style={{ width: 110 }} />
      </colgroup>
      <thead>
        <tr style={{ background: "var(--muted)", borderColor: "var(--border)" }} className="border-b">
          <th
            rowSpan={2}
            style={{ color: "var(--muted-foreground)", borderColor: "var(--border)" }}
            className="text-left px-2 py-2 font-semibold w-[70px] border-r align-bottom"
          >
            Product ID
          </th>
          <th
            rowSpan={2}
            style={{ color: "var(--muted-foreground)", borderColor: "var(--border)" }}
            className="text-left px-2 py-2 font-semibold border-r align-bottom"
          >
            Name
          </th>
          <th
            rowSpan={2}
            style={{ color: "var(--muted-foreground)", borderColor: "var(--border)" }}
            className="text-right px-2 py-2 font-semibold border-r align-bottom"
          >
            Target ASP
          </th>
          <th
            colSpan={2}
            style={{ color: "var(--muted-foreground)", borderColor: "var(--border)" }}
            className="text-center px-2 py-2 font-semibold text-[10px] uppercase tracking-wider border-r border-b"
          >
            Inventory
          </th>
          <th
            colSpan={monthCount}
            style={{ color: "var(--muted-foreground)", borderColor: "var(--border)" }}
            className="text-center px-2 py-2 font-semibold text-[10px] uppercase tracking-wider border-b border-r"
          >
            Sales
          </th>
          <th rowSpan={2} style={{ borderColor: "var(--border)" }} className="px-2 py-2 align-bottom" />
        </tr>
        <tr style={{ background: "var(--muted)", borderColor: "var(--border)" }} className="border-b">
          <th style={{ color: "var(--muted-foreground)" }} className="text-right px-2 py-2 font-semibold w-[60px]">
            {channelName}
          </th>
          <th
            style={{ color: "var(--muted-foreground)", borderColor: "var(--border)" }}
            className="text-right px-2 py-2 font-semibold w-[55px] border-r"
          >
            Total
          </th>
          {[...(months ?? [])].reverse().map((m) => (
            <th key={m} style={{ color: "var(--muted-foreground)" }} className="text-right px-2 py-2 font-semibold w-[60px]">
              {formatMonthLabel(m)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {!products && loading && (
          <tr>
            <td colSpan={6 + monthCount} className="px-3 py-8 text-center" style={{ color: "var(--muted-foreground)" }}>
              Loading…
            </td>
          </tr>
        )}
        {products && products.length === 0 && (
          <tr>
            <td colSpan={6 + monthCount} className="px-3 py-8 text-center" style={{ color: "var(--muted-foreground)" }}>
              {emptyMessage}
            </td>
          </tr>
        )}
        {products?.map((p) => (
          <tr
            key={p.external_id}
            style={{
              borderColor: "var(--border)",
              background: p.in_plan ? "color-mix(in srgb, var(--secondary) 8%, transparent)" : undefined,
            }}
            className="border-b hover:bg-muted/40"
          >
            <td
              className="px-2 py-2 tabular-nums border-r"
              style={{ color: "var(--muted-foreground)", borderColor: "var(--border)" }}
            >
              {p.external_id}
            </td>
            <td className="px-2 py-2 border-r" style={{ color: "var(--foreground)", borderColor: "var(--border)" }}>
              <span title={p.name}>{p.short_name || p.name}</span>
            </td>
            <td
              className="px-2 py-2 text-right tabular-nums border-r"
              style={{ color: "var(--foreground)", borderColor: "var(--border)" }}
            >
              {p.target_asp ? formatQty(p.target_asp.price) : <span style={{ color: "var(--muted-foreground)" }}>—</span>}
            </td>
            <td className="px-2 py-2 text-right tabular-nums" style={{ color: "var(--foreground)" }}>
              {formatQty(p.channel_stock)}
            </td>
            <td
              className="px-2 py-2 text-right tabular-nums border-r"
              style={{ color: "var(--foreground)", borderColor: "var(--border)" }}
            >
              {formatQty(p.total_stock)}
            </td>
            {[...p.monthly_sales].reverse().map((s) => (
              <td key={s.month} className="px-2 py-2 text-right tabular-nums" style={{ color: "var(--foreground)" }}>
                <div>{formatQty(s.quantity)}</div>
                <div className="text-[10px]" style={{ color: "var(--muted-foreground)" }}>
                  {formatRevenue(s.revenue)}
                </div>
              </td>
            ))}
            <td className="px-2 py-2 text-center border-l" style={{ borderColor: "var(--border)" }}>
              {renderAction(p)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface FooterProps {
  page: number;
  totalPages: number;
  totalCount: number;
  loaded: boolean;
  loading: boolean;
  onPageChange: (page: number) => void;
}

export function ProductPickerFooter({ page, totalPages, totalCount, loaded, loading, onPageChange }: FooterProps) {
  return (
    <div
      className="px-5 py-3 border-t flex items-center justify-between text-xs"
      style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
    >
      <span>
        {loaded ? `${totalCount} product${totalCount === 1 ? "" : "s"} • page ${page} of ${Math.max(1, totalPages)}` : ""}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={!loaded || page <= 1 || loading} onClick={() => onPageChange(page - 1)}>
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!loaded || page >= totalPages || loading}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
